import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [meta, setMeta] = useState({ colleges: [], member_types: [] });
  const [form, setForm] = useState({
    email: "", password: "",
    first_name: "", last_name: "",
    college: "", grad_year: "",
    member_type: "", bio: "", linkedin_url: "",
    photo_data: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) {
      toast.error("Photo must be under 800KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("photo_data", reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({
        ...form,
        grad_year: parseInt(form.grad_year, 10),
      });
      toast.success("Welcome to CCNEO Connect");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const gradYears = Array.from({ length: 2035 - 1955 + 1 }, (_, i) => 2035 - i);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <Link to="/login" className="flex items-center gap-3 mb-6" data-testid="register-brand">
          <div className="w-10 h-10 rounded-xl bg-[#B31B1B] text-white grid place-items-center text-xl font-bold" style={{ fontFamily: "Georgia, serif" }}>C</div>
          <div>
            <div className="font-extrabold text-lg">CCNEO Connect</div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Cornell Club NEO</div>
          </div>
        </Link>

        <div className="bg-white rounded-2xl border border-border cornell-shadow p-6">
          <h1 className="text-2xl font-extrabold tracking-tight">Join CCNEO Connect</h1>
          <p className="text-sm text-muted-foreground mt-1">Cornell alumni & students of Northeast Ohio.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="register-first-name-input" />
              </div>
              <div>
                <Label>Last name</Label>
                <Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="register-last-name-input" />
              </div>
            </div>

            <div>
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email-input" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>College</Label>
                <Select value={form.college} onValueChange={(v) => set("college", v)}>
                  <SelectTrigger data-testid="register-college-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {meta.colleges.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`register-college-opt-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Graduation year</Label>
                <Select value={String(form.grad_year || "")} onValueChange={(v) => set("grad_year", v)}>
                  <SelectTrigger data-testid="register-grad-year-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {gradYears.map((y) => (
                      <SelectItem key={y} value={String(y)} data-testid={`register-grad-year-opt-${y}`}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Member type</Label>
              <Select value={form.member_type} onValueChange={(v) => set("member_type", v)}>
                <SelectTrigger data-testid="register-member-type-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>
                  {meta.member_types.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`register-member-type-opt-${t.replace(/\s+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Short bio <span className="text-muted-foreground text-xs">({form.bio.length}/150)</span></Label>
              <Textarea maxLength={150} rows={2} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Software engineer at Cleveland Clinic…" data-testid="register-bio-input" />
            </div>
            <div>
              <Label>LinkedIn URL (optional)</Label>
              <Input type="url" value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="register-linkedin-input" />
            </div>
            <div>
              <Label>Profile photo (optional)</Label>
              <div className="flex items-center gap-3">
                {form.photo_data ? (
                  <img src={form.photo_data} alt="preview" className="w-14 h-14 rounded-full object-cover ring-1 ring-black/10" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#B31B1B]/10 text-[#B31B1B] grid place-items-center font-semibold">
                    {(form.first_name[0] || "").toUpperCase()}{(form.last_name[0] || "").toUpperCase() || "?"}
                  </div>
                )}
                <Input type="file" accept="image/*" onChange={onPhoto} className="max-w-xs" data-testid="register-photo-input" />
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#B31B1B] bg-[#B31B1B]/5 border border-[#B31B1B]/20 px-3 py-2 rounded-md" data-testid="register-error">{error}</div>
            )}

            <Button type="submit" disabled={loading} className="w-full bg-[#B31B1B] hover:bg-[#8f1616] rounded-full h-11" data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <div className="mt-4 text-sm text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#B31B1B] hover:underline" data-testid="register-to-login-link">Sign in</Link>
          </div>
        </div>
        <p className="mt-6 text-center text-[10px] text-muted-foreground">Powered by SocialImpactCLE</p>
      </div>
    </div>
  );
}
